import { useEffect, useState, useCallback } from 'react';
import { CheckCircle, ErrorOutline, Cancel } from '@groww-tech-pvt/icon-store/mi';

type ToastType = 'success' | 'error';

interface ToastItem {
 id: number;
 type: ToastType;
 description: string;
}

type ToastListener = (toasts: ToastItem[]) => void;

let toastIdCounter = 0;
let toastList: ToastItem[] = [];
let listeners: ToastListener[] = [];

const TOAST_DURATION = 4000;

const emitChange = () => {
 listeners.forEach(listener => listener([...toastList]));
};

const addToast = (type: ToastType, description: string) => {
 const id = ++toastIdCounter;
 const newToast: ToastItem = { id, type, description };

 toastList = [...toastList, newToast];
 emitChange();

 setTimeout(() => {
  removeToast(id);
 }, TOAST_DURATION);
};

const removeToast = (id: number) => {
 toastList = toastList.filter(t => t.id !== id);
 emitChange();
};

/**
 * Toast utility — call from anywhere without needing React context.
 *
 * Usage:
 *   import { onbToast } from 'src/components/Toast';
 *   onbToast.success({ description: 'Done!' });
 *   onbToast.error({ description: 'Something went wrong.' });
 */
export const onbToast = {
 success: ({ description }: { description: string }) => addToast('success', description),
 error: ({ description }: { description: string }) => addToast('error', description),
};

const TOAST_STYLES: Record<ToastType, { bg: string; icon: JSX.Element }> = {
 success: {
  bg: 'backgroundPositiveSubtle',
  icon: <CheckCircle size={20} className="contentPositive" />,
 },
 error: {
  bg: 'backgroundNegativeSubtle',
  icon: <ErrorOutline size={20} className="contentNegative" />,
 },
};

/**
 * Render this once at the app root (e.g. inside OnboardingProvider).
 * It listens to the global toast list and renders active toasts.
 */
export const ToastContainer = () => {
 const [toasts, setToasts] = useState<ToastItem[]>([]);

 useEffect(() => {
  listeners.push(setToasts);

  return () => {
   listeners = listeners.filter(l => l !== setToasts);
  };
 }, []);

 const handleDismiss = useCallback((id: number) => {
  removeToast(id);
 }, []);

 if (toasts.length === 0) return null;

 return (
  <div className="fixed top-4 left-1/2 -translate-x-1/2 z-10001 flex flex-col gap-2 w-full max-w-sm px-4">
   {toasts.map(toast => {
    const style = TOAST_STYLES[toast.type];

    return (
     <div
      key={toast.id}
      className={`${style.bg} flex items-start gap-3 px-4 py-3 rounded-lg shadow-lg animate-fadeIn`}
      role="alert"
     >
      <span className="mt-0.5 shrink-0">{style.icon}</span>
      <p className="bodyBase contentPrimary flex-1">{toast.description}</p>
      <button
       onClick={() => handleDismiss(toast.id)}
       className="shrink-0 cur-po mt-0.5"
       aria-label="Dismiss"
      >
       <Cancel size={16} className="contentSecondary" />
      </button>
     </div>
    );
   })}
  </div>
 );
};
